import React, { ReactNode, useMemo, useState } from "react";

export const log = (...args: any[]) => {
  // eslint-disable-next-line no-console
  if (NODE_ENV === "development") console.debug(...args);
};

export const track =
  (commonProps: SegmentProps, setCommonProps: SetCommonPropsFn): TrackFn =>
  ({ eventName, action, element, props, commonProps: newCommonProps }) => {
    // Similarly to React's setState, user can pass in a newCommonProps object, or
    // a function that merges previously-set commonProps with newCommonProps.
    const resolvedCommonProps =
      typeof newCommonProps === "function"
        ? newCommonProps(commonProps)
        : { ...commonProps, ...newCommonProps };

    // Update common props
    setCommonProps(resolvedCommonProps);

    const mergedProps = {
      ...resolvedCommonProps,
      ...props,
      action,
      element: element || null,
    };
    window.analytics.track(eventName, mergedProps);
    log("[Segment.track]", eventName, mergedProps);
  };

export const identify = (setCommonProps: SetCommonPropsFn) => (user: User) => {
  const userId = user.id;
  const userCommonProps = {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    cityId: user.cityId,
  };

  // Set common user Segment props for the logged-in user
  setCommonProps(prevState => ({
    ...prevState,
    userId: user.id,
    ...userCommonProps,
  }));

  // Set Segment user identity
  window.analytics.identify(userId, userCommonProps);
  log("[Segment.identify]", userId, userCommonProps);
};

export const reset =
  (defaultCommonProps: SegmentProps, setCommonProps: SetCommonPropsFn) =>
  () => {
    window.analytics.reset();
    log("[Segment.reset]");
    setCommonProps(defaultCommonProps);
  };

export const wrapProvider =
  (Context: React.Context<SegmentContext>, defaultValue: SegmentContext) =>
  ({ children }: { children: ReactNode }) => {
    const [commonProps, setCommonProps] = useState(defaultValue.commonProps);
    const segmentContext = useMemo(
      () => ({
        track: track(commonProps, setCommonProps),
        identify: identify(setCommonProps),
        reset: reset(defaultValue.commonProps, setCommonProps),
        commonProps,
        setCommonProps,
      }),
      [commonProps, setCommonProps],
    );

    return (
      <Context.Provider value={segmentContext}>{children}</Context.Provider>
    );
  };
